import type { ReturningArmy, RecentCombat } from '../../types/province';
import { formatNumber } from '../../utils/format';
import { tickToGameDate, formatDay } from '../../utils/gameTime';
import { COLORS } from '../../constants/theme';

/** Shared by MobileThroneShell/DesktopThroneShell's Armies Away card — pure formatting, not chrome. */
export function summarizeArmyUnits(army: ReturningArmy): string | undefined {
  const parts: string[] = [];
  if (army.soldiers > 0) parts.push(`${formatNumber(army.soldiers)} soldiers`);
  if (army.offenseSpecialists > 0) parts.push(`${formatNumber(army.offenseSpecialists)} offense`);
  if (army.elites > 0) parts.push(`${formatNumber(army.elites)} elite`);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

export function ordinalSuffix(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/** Signed trade-balance color. */
export function resolveTradeColor(tradeBalance: number): string | undefined {
  if (tradeBalance > 0) return '#5dcc5d';
  if (tradeBalance < 0) return COLORS.negative;
  return undefined;
}

/** Recent Activity card row content, ported verbatim from the deleted ProvinceNews.tsx. */
export function describeCombatEvent(event: RecentCombat): { color: string; message: string } {
  const isVictory = event.outcome === 'victory';
  const color = isVictory ? '#5dcc5d' : event.outcome === 'defeat' ? '#ff7766' : '#e0d5c8';
  const message = isVictory
    ? `Victory against ${event.targetProvinceId} - Captured ${event.landCaptured} acres`
    : `Defeat against ${event.targetProvinceId}`;
  return { color, message };
}

/**
 * Kingdoms have no custom-name system yet — `kingdomName` is always synthesized server-side as
 * `"Kingdom " + kingdomId` (`ProvinceDtoMapper.mapLocation`), where `kingdomId` is itself the same
 * `"kingdom:island"` string the identity header separately renders in parentheses. Appending the
 * parenthesized location after the raw name therefore always duplicates it (e.g.
 * "Kingdom 28:48 (28:48)"). Strip the embedded id so only the parenthesized form remains
 * ("Kingdom (28:48)") — falls through untouched once real custom kingdom names exist and no
 * longer end in the id.
 */
export function stripEmbeddedLocation(kingdomName: string | undefined, kingdomId: string): string {
  if (!kingdomName) return 'Kingdom';
  const suffix = ` ${kingdomId}`;
  return kingdomName.endsWith(suffix) ? kingdomName.slice(0, -suffix.length) : kingdomName;
}

/** Current Utopian date, e.g. "February 5th, YR2" — matches `AttackResult.tsx`'s existing phrasing. */
export function formatCurrentGameDate(currentTick: number): string {
  const date = tickToGameDate(currentTick);
  return `${date.month} ${formatDay(date.day)}, YR${date.year}`;
}
